/**
 * funnelGroups.ts — the eleven board stages, grouped by WHO THE DEAL IS WAITING ON.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY GROUP AT ALL (2026-08-30, CTO: "nicely outline a funnel and show how many
 * are in each stage"). Eleven stage chips in a flat wrapped row is a list, not a
 * funnel: it tells you the names of the stages and nothing about the shape of
 * the business. Grouped, the same numbers answer the question an admin actually
 * opens this screen with — where is the work, and who is holding it up.
 *
 * ⚠️ THE GROUPING AXIS IS "WHO IS BLOCKED", NOT "WHAT HAPPENED". That is a
 * deliberate choice over the obvious alternative of grouping by lifecycle
 * (new → qualifying → closed). Lifecycle groups restate the board's own left-to-
 * right order and add nothing you could not already see. "Waiting on us" versus
 * "waiting on the patient" is the split that changes what somebody does next:
 * one is a staffing problem, the other is a chasing problem, and today they sit
 * interleaved across the board with no way to total them.
 *
 * ⚠️ EVERY STAGE APPEARS EXACTLY ONCE, and a test pins that against DEAL_STAGES.
 * A stage missing from a group would silently vanish from the funnel totals —
 * the numbers would still add up among themselves and simply describe a smaller
 * business than the one that exists. A stage in two groups would double-count.
 * Neither is visible by inspection, which is why it is asserted rather than
 * reviewed.
 */
import type { DealStage } from '../api/types';

export interface FunnelGroup {
  key: string;
  label: string;
  /** One line on what this group MEANS operationally — the modal renders it. */
  hint: string;
  stages: readonly DealStage[];
}

export const FUNNEL_GROUPS: readonly FunnelGroup[] = [
  {
    key: 'new',
    label: 'New',
    hint: 'Arrived, nobody has worked them yet.',
    /**
     * ⚠️ `mql` ALONE SINCE 2026-09-05, and the stage that left is the point of
     * the restructure. `marketing_funnel` used to sit here, and under this
     * group's hint it claimed nobody had worked those patients yet. The exact
     * opposite is true: a card reaches that lane — now `unreachable` — after
     * 3 clinician calls and 3 admin calls have all gone unanswered, or (since
     * 2026-09-22) because an ADMIN read the same situation off a card nobody
     * was formally ringing and said so by hand. Either way it
     * was the most-worked cohort on the board, filed under "New" and inflating
     * the one number an admin reads to decide whether there is fresh work.
     */
    stages: ['mql'],
  },
  {
    key: 'working',
    label: 'Being worked',
    hint: 'Someone is actively on these.',
    // `callback_requested` alone since the merge: someone is ringing them.
    // `eligibility_in_progress` used to join it here, but the lane it merged
    // into is a clinician's REVIEW QUEUE, which is a hold-up rather than
    // activity — see the `with_us` group below.
    stages: ['callback_requested'],
  },
  {
    key: 'with_us',
    label: 'Waiting on us',
    hint: 'The patient has done their part. A clinician or an admin is the hold-up.',
    /**
     * `clinical_review` is the merge of the two stages that used to straddle
     * this boundary — `awaiting_clinical_review` was already here, and
     * `eligibility_in_progress` sat in "Being worked". One lane cannot be in
     * two groups, and the server settles which: `clinical_review` means "a
     * clinician owns this questionnaire, whether or not the patient has paid
     * yet". That is us holding it up, so the whole lane counts here.
     */
    stages: [
      'clinical_review',
      'awaiting_clinician_followup',
      'awaiting_admin_followup',
      'pending_admin_review',
    ],
  },
  {
    key: 'with_patient',
    label: 'Waiting on the patient',
    hint: 'We have done our part. These are the ones worth chasing.',
    /**
     * `warm_lead` joined on 2026-09-07 with the authorisation gate, and this
     * group's hint already described it word for word: the questionnaire is
     * finished, the plan is on screen, and the only outstanding thing is the
     * patient authorising a card. "The ones worth chasing" is precisely what
     * the CTO asked for — "admins or clinicians can then reach out to them
     * while seeing where they stopped, get real intention".
     *
     * NOT "New": that group is `mql` alone, and filing a finished questionnaire
     * there would repeat the exact mistake the 2026-09-05 restructure fixed
     * when it pulled `marketing_funnel` out of it — inflating the one number an
     * admin reads to decide whether there is fresh work, with a cohort that has
     * already been through the whole form.
     *
     * NOT "Waiting on us" either: nobody here is a hold-up on our side. There
     * is no clinician queue behind a warm lead and no admin has been asked for
     * anything — that is the entire meaning of the gate.
     */
    stages: ['warm_lead', 'plan_sent'],
  },
  {
    key: 'unreachable',
    label: 'Could not reach',
    hint: 'Nobody could reach them. Most come back on their own; a few are marked here by an admin.',
    /**
     * ITS OWN GROUP, DELIBERATELY, AND NEITHER OF THE TWO OBVIOUS HOMES.
     *
     * Not "New" — that is where it used to be, under the old
     * `marketing_funnel` name, and it is the mislabel this restructure exists
     * to end (see the `new` group above).
     *
     * Not "Waiting on the patient" either, which is the tempting one: it is
     * literally true that the patient is the hold-up, but that group's hint
     * calls its members "the ones worth chasing" and drives an admin's chase
     * list. These are precisely the patients chasing has already been
     * exhausted on — six unanswered attempts, or an admin's own judgement that
     * the phone is not being answered (2026-09-22). Folding them in would tell
     * an admin to do again the thing that has demonstrably not worked, and
     * would swell a count that is read as a to-do list.
     *
     * It is still OPEN, so it stays out of `closed` and keeps counting toward
     * the `openShare` denominator — these people have not been written off.
     */
    stages: ['unreachable'],
  },
  {
    key: 'closed',
    label: 'Closed',
    hint: 'No longer in play.',
    /**
     * `closed_duplicate` sits here (2026-09-06) because a merged card IS out of
     * play — but note what that does to the two numbers around it. The Closed
     * group's total now includes merges, while the LOST lane's own count stays
     * clean, which is the whole reason the server gave duplicates a stage of
     * their own instead of `closed_lost` + a reason string: a merged patient
     * converted on the other card and must never be counted as a loss.
     *
     * It also leaves the group's `openShare` denominator honest — duplicates are
     * subtracted from the open pipeline along with the other closes, so a board
     * that merges a lot of cards does not read as one with a shrinking funnel.
     */
    stages: ['closed_won', 'closed_lost', 'closed_duplicate'],
  },
];

/** Total across a group, from the tenant-wide counts (never the loaded page). */
export function groupTotal(group: FunnelGroup, counts: Partial<Record<DealStage, number>> | null): number {
  if (!counts) return 0;
  return group.stages.reduce((sum, stage) => sum + (counts[stage] ?? 0), 0);
}

/**
 * The share of the OPEN pipeline a number represents, 0..1.
 *
 * ⚠️ THE DENOMINATOR EXCLUDES CLOSED DEALS, and that is the whole point of
 * having a helper rather than dividing inline. Closed_won and closed_lost
 * accumulate forever, so measuring against every deal ever created makes the
 * live pipeline look like a rounding error and the bars stop moving. Against
 * the OPEN total, a bar that grows means work is piling up — which is the only
 * reading anyone would act on.
 */
export function openShare(value: number, counts: Partial<Record<DealStage, number>> | null): number {
  if (!counts) return 0;
  const closed = FUNNEL_GROUPS.find((g) => g.key === 'closed');
  const closedTotal = closed ? groupTotal(closed, counts) : 0;
  const all = Object.values(counts).reduce((sum: number, n) => sum + (n ?? 0), 0);
  const open = all - closedTotal;
  return open > 0 ? value / open : 0;
}
